/**
 * PatientFormConfirmation — Paso final del wizard: datos del paciente + confirmación
 * Diseño: formulario progresivo con grupos semánticos, resumen sticky, validación al blur.
 * Lógica de negocio intacta (Supabase, validación, submit, verificación de disponibilidad).
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useBooking } from '../BookingContext';
import { BookingService, isPatientFacingBookingError, SLOT_TAKEN_MESSAGE } from '../../../services/booking-fixed.service';
import { supabase } from '../../../lib/supabase/client';
import { getSessionPrice } from '../../../lib/utils/pricing';
import PaymentMethodSelector from '../PaymentMethodSelector';
import { EmailService } from '../../../services/email.service';
import { CalendarService } from '../../../services/calendar-production.service';
import { useToast } from '../../ui/ToastProvider';
import {
  loadPatientFormDraft,
  savePatientFormDraft,
  hasPatientFormDraft,
  clearPatientFormDraft,
} from '../bookingPersistence';
import { CLINIC_OFFICE_ADDRESS } from '../../../data/clinicPaymentInfo';
import '../BookingSynthesis.css';
import '../ProcessingOverlay.css';
import './PatientFormConfirmation.css';

interface PatientFormConfirmationProps {
  onPrevious: () => void;
  onComplete: (bookingId: string, meetLink?: string) => void;
}

type GeneroType = 'femenino' | 'masculino' | 'no-binario' | 'prefiero-no-decir';
type ComunicacionType = 'email' | 'telefono' | 'whatsapp';
type ProcessingStepKey = 'validating' | 'creating' | 'sending' | 'completed';

// Devuelve las iniciales del nombre (máx. 2 letras)
function getInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const GENERO_OPTIONS: { value: GeneroType; label: string }[] = [
  { value: 'femenino', label: 'Femenino' },
  { value: 'masculino', label: 'Masculino' },
  { value: 'no-binario', label: 'No binario' },
  { value: 'prefiero-no-decir', label: 'Prefiero no decir' },
];

const MOTIVO_OPTIONS = [
  'Ansiedad y estrés',
  'Depresión y estado de ánimo',
  'Problemas de pareja',
  'Problemas familiares',
  'Autoestima y confianza',
  'Duelo y pérdidas',
  'Adicciones',
  'Trastornos alimenticios',
  'Problemas laborales',
  'Desarrollo personal',
  'Otro',
];

const PROCESSING_STEPS: { key: ProcessingStepKey; label: string; icon: string }[] = [
  { key: 'validating', label: 'Verificando disponibilidad', icon: 'fas fa-shield-alt' },
  { key: 'creating',  label: 'Creando tu cita',           icon: 'fas fa-calendar-plus' },
  { key: 'sending',   label: 'Enviando confirmación',      icon: 'fas fa-envelope' },
  { key: 'completed', label: 'Cita confirmada',            icon: 'fas fa-check-circle' },
];

// Grupos del formulario — se usan para el indicador de progreso mobile
const FORM_GROUPS = ['personal', 'reason', 'legal'] as const;
type GroupKey = typeof FORM_GROUPS[number];

const PatientFormConfirmation: React.FC<PatientFormConfirmationProps> = ({
  onPrevious,
  onComplete,
}) => {
  const { state, setMetodoPagoDeclarado, setService } = useBooking();
  const toast = useToast();

  // FUNC-105 + FUNC-131: el precio depende del método de pago elegido en este
  // paso y de la fecha (promo online) → se recalcula acá con la misma matriz
  // que create_consultation server-side. Cambiar el método actualiza el total al instante.
  const precioFinal = state.selectedService
    ? getSessionPrice({
        tipo: state.selectedService.therapyType,
        duracion: state.selectedService.duration,
        modalidad: state.selectedService.modality,
        metodo: state.metodoPagoDeclarado,
        fechaHora: state.selectedTimeSlot?.date,
      }) ?? state.selectedService.price
    : 0;

  // ── Estado de procesamiento ────────────────────────────────
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<ProcessingStepKey>('validating');

  // ── Estado del formulario ─────────────────────────────────
  // Semilla desde el draft persistido (UX_VISION P1-5). NO se restauran los checkboxes
  // de consentimiento (re-consentimiento fresco) ni datos de contacto de emergencia.
  const [formData, setFormData] = useState(() => {
    const draft = loadPatientFormDraft();
    return {
      nombre: draft?.nombre ?? '',
      apellido: draft?.apellido ?? '',
      email: draft?.email ?? '',
      telefono: draft?.telefono ?? '',
      genero: (draft?.genero as GeneroType) ?? ('prefiero-no-decir' as GeneroType),
      nombrePareja: draft?.nombrePareja ?? '',
      direccion: draft?.direccion ?? '',
      antecedentesMedicos: '',
      medicamentos: '',
      emergenciaContacto: '',
      emergenciaTelefono: '',
      preferenciaComunicacion: (draft?.preferenciaComunicacion as ComunicacionType) ?? ('email' as ComunicacionType),
      motivoConsulta: draft?.motivoConsulta ?? '',
      // FUNC-114 (Ley 21.719): re-consentimiento fresco en cada visita, igual
      // que el resto de los checkboxes — no se restauran desde el draft.
      consentimientoDatosClinicos: false,
      consentimientoContactoMarketing: false,
      terminosCondiciones: false,
    };
  });
  const [draftRestored, setDraftRestored] = useState(() => hasPatientFormDraft());

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // touched: registra qué campos ya fueron tocados (para validación al blur)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // ── UI state ──────────────────────────────────────────────
  const [activeGroup, setActiveGroup] = useState<GroupKey>('personal');
  const [summaryCollapsed, setSummaryCollapsed] = useState(true); // mobile: arranca cerrado

  // ── Validación ────────────────────────────────────────────
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.nombre.trim())         errors.nombre = 'El nombre es requerido';
    if (!formData.apellido.trim())       errors.apellido = 'El apellido es requerido';
    if (!formData.email.trim())          errors.email = 'El email es requerido';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email))
                                         errors.email = 'El email no es válido';
    if (!formData.telefono.trim())       errors.telefono = 'El teléfono es requerido';
    if (state.selectedService?.modality === 'domicilio' && !formData.direccion.trim())
                                         errors.direccion = 'La dirección es requerida para consultas a domicilio';
    if (!formData.motivoConsulta.trim()) errors.motivoConsulta = 'El motivo de consulta es requerido';
    if (!formData.consentimientoDatosClinicos)
      errors.consentimientoDatosClinicos = 'Debe autorizar el tratamiento de sus datos clínicos';
    // Contacto/marketing es opt-in: nunca bloquea el envío del formulario.
    if (!formData.terminosCondiciones)   errors.terminosCondiciones = 'Debe aceptar los términos y condiciones';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validar campo individual al blur
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'nombre':   return value.trim() ? '' : 'El nombre es requerido';
      case 'apellido': return value.trim() ? '' : 'El apellido es requerido';
      case 'email':
        if (!value.trim()) return 'El email es requerido';
        if (!/\S+@\S+\.\S+/.test(value)) return 'El email no es válido';
        return '';
      case 'telefono':       return value.trim() ? '' : 'El teléfono es requerido';
      case 'direccion':
        if (state.selectedService?.modality === 'domicilio' && !value.trim())
          return 'La dirección es requerida para consultas a domicilio';
        return '';
      case 'motivoConsulta': return value.trim() ? '' : 'El motivo de consulta es requerido';
      default: return '';
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const error = validateField(field, value);
      setFormErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, (formData as any)[field]);
    setFormErrors((prev) => ({ ...prev, [field]: error }));
  };

  // ── Autosave del draft (UX_VISION P1-5) ───────────────────
  // Debounce 800ms. Pausado durante el submit (evita que un timer tardío re-escriba
  // el blob después de que `onComplete` lo limpió). `savePatientFormDraft` filtra los
  // campos sensibles/de terceros y no escribe nada si el paciente no tipeó todavía.
  useEffect(() => {
    if (isProcessing) return;
    const id = window.setTimeout(() => savePatientFormDraft(formData), 800);
    return () => window.clearTimeout(id);
  }, [formData, isProcessing]);

  // Un draft "existe" si se restauró al montar o si el paciente ya tipeó algo persistible.
  const draftExists =
    draftRestored ||
    [
      formData.nombre,
      formData.apellido,
      formData.email,
      formData.telefono,
      formData.direccion,
      formData.motivoConsulta,
      formData.nombrePareja,
    ].some((v) => v.trim());

  const handleDiscardDraft = () => {
    clearPatientFormDraft();
    setFormData((prev) => ({
      ...prev,
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      genero: 'prefiero-no-decir',
      nombrePareja: '',
      direccion: '',
      preferenciaComunicacion: 'email',
      motivoConsulta: '',
    }));
    setFormErrors({});
    setTouched({});
    setDraftRestored(false);
    toast.info('Listo, borramos lo que habías guardado en este dispositivo.');
  };

  // Determina la clase CSS del input según estado de validación
  const inputClass = (field: string): string => {
    if (!touched[field]) return 'pfc-input';
    if (formErrors[field]) return 'pfc-input pfc-input--invalid';
    const value = (formData as any)[field];
    if (value !== '' && value !== undefined) return 'pfc-input pfc-input--valid';
    return 'pfc-input';
  };

  const textareaClass = (field: string): string => {
    if (!touched[field]) return 'pfc-textarea';
    if (formErrors[field]) return 'pfc-textarea pfc-textarea--invalid';
    const value = (formData as any)[field];
    if (value?.trim()) return 'pfc-textarea pfc-textarea--valid';
    return 'pfc-textarea';
  };

  // ── Progreso de grupos (para los dots mobile) ─────────────
  const isGroupComplete = (group: GroupKey): boolean => {
    switch (group) {
      case 'personal':
        return !!(
          formData.nombre.trim() &&
          formData.apellido.trim() &&
          formData.email.trim() &&
          /\S+@\S+\.\S+/.test(formData.email) &&
          formData.telefono.trim() &&
          (state.selectedService?.modality !== 'domicilio' || formData.direccion.trim())
        );
      case 'reason':
        return !!formData.motivoConsulta.trim();
      case 'legal':
        return formData.consentimientoDatosClinicos && formData.terminosCondiciones;
    }
  };

  const isFormValid = (): boolean =>
    !!(
      formData.nombre &&
      formData.apellido &&
      formData.email &&
      formData.telefono &&
      formData.motivoConsulta &&
      formData.consentimientoDatosClinicos &&
      formData.terminosCondiciones &&
      (state.selectedService?.modality !== 'domicilio' || formData.direccion.trim())
    );

  // ── Submit principal ──────────────────────────────────────
  const handleConfirm = async () => {
    // Marcar todos los campos como tocados antes de validar
    const allFields = ['nombre', 'apellido', 'email', 'telefono', 'motivoConsulta'];
    if (state.selectedService?.modality === 'domicilio') allFields.push('direccion');
    setTouched(allFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    if (!validateForm()) {
      // Scroll al primer error
      const firstError = document.querySelector('.pfc-input--invalid, .pfc-textarea--invalid');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsProcessing(true);
    setProcessingStep('validating');

    try {
      if (!state.selectedTherapist || !state.selectedService || !state.selectedTimeSlot || !state.patientVerification) {
        throw new Error('Faltan datos requeridos para completar la reserva');
      }

      // Step 1: Verificar disponibilidad justo antes de crear
      const isAvailable = await CalendarService.validateSlotAvailability(
        state.selectedTherapist.id,
        state.selectedTimeSlot.date,
        state.selectedTimeSlot.time
      );

      if (!isAvailable) {
        throw new Error(SLOT_TAKEN_MESSAGE);
      }

      // Step 2: Crear la cita en Supabase
      setProcessingStep('creating');
      const bookingResult = await BookingService.processBooking({
        therapist: state.selectedTherapist,
        service: { ...state.selectedService, price: precioFinal },
        timeSlot: state.selectedTimeSlot,
        patientData: {
          ...formData,
          rut: state.patientVerification?.rut || '',
        },
        metodoPagoDeclarado: state.metodoPagoDeclarado,
      });

      if (!bookingResult.success) {
        throw new Error(bookingResult.error || 'Error procesando la reserva');
      }

      // Step 3: Enviar emails de confirmación (no bloquea el flujo si falla)
      setProcessingStep('sending');

      await EmailService.sendBookingConfirmation({
        bookingId:        bookingResult.bookingId,
        patientName:      `${formData.nombre} ${formData.apellido}`,
        patientEmail:     formData.email,
        patientPhone:     formData.telefono,
        therapistName:    state.selectedTherapist!.name,
        therapistEmail:   state.selectedTherapist!.email ?? 'angeldiazdiaz01@gmail.com',
        therapistRegistroSuperintendencia: state.selectedTherapist!.professionalInfo?.registroSuperintendencia,
        therapistNumeroLicencia: state.selectedTherapist!.professionalInfo?.licenseNumber,
        serviceName:      state.selectedService!.name,
        therapyType:      state.selectedService!.therapyType,
        date:             state.selectedTimeSlot!.date,
        time:             state.selectedTimeSlot!.time,
        duration:         state.selectedService!.duration,
        price:            precioFinal,
        modality:         state.selectedService!.modality,
        nombrePareja:     formData.nombrePareja.trim() || undefined,
        direccion:        formData.direccion.trim() || undefined,
        meetLink:         bookingResult.meetLink,
        metodoPagoDeclarado: state.metodoPagoDeclarado,
      });

      // Notificar booking completado: limpia cache e invalida calendarios en tiempo real
      if ((window as any).notifyBookingCompleted) {
        (window as any).notifyBookingCompleted(
          state.selectedTherapist.id,
          state.selectedTimeSlot.date
        );
      }

      // El voucher de éxito lee el precio del contexto — se alinea con lo cobrado.
      if (state.selectedService && precioFinal !== state.selectedService.price) {
        setService({ ...state.selectedService, price: precioFinal });
      }

      onComplete(bookingResult.bookingId, bookingResult.meetLink);
    } catch (error: any) {
      // Sin exponer el error crudo de Supabase al paciente (regla de CLAUDE.md).
      console.error('Error al confirmar cita:', error);
      const msg: string = error?.message || '';

      if (isPatientFacingBookingError(msg)) {
        // FUNC-121: el servicio ya devuelve solo mensajes propios (rate-limit
        // SEC-059, conflicto SEC-062, horario ocupado, bloqueo, genérico) —
        // se muestran tal cual.
        toast.error(msg);
      } else if (msg.includes('network') || msg.includes('connection')) {
        toast.error('Problema de conexión. Revisá tu internet e intentá de nuevo.');
      } else {
        toast.error('No pudimos procesar tu cita. Intentá de nuevo o escribinos al +56 9 7800 9119.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString + 'T12:00:00').toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  // ── Overlay de procesamiento ──────────────────────────────
  if (isProcessing) {
    return (
      <motion.div
        className="booking-processing-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="processing-content">
          <div className="processing-animation">
            <div className="processing-spinner" />
          </div>
          <h2 className="processing-title">Confirmando tu cita</h2>
          <p className="processing-subtitle">Por favor espera mientras procesamos tu solicitud</p>
          <div className="processing-steps">
            {PROCESSING_STEPS.map((step, index) => {
              const currentIndex = PROCESSING_STEPS.findIndex((s) => s.key === processingStep);
              const isActive = step.key === processingStep;
              const isCompleted = currentIndex > index;
              return (
                <motion.div
                  key={step.key}
                  className={`processing-step${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="step-icon">
                    {isCompleted ? (
                      <i className="fas fa-check" />
                    ) : (
                      <i className={step.icon} />
                    )}
                  </div>
                  <span className="step-label">{step.label}</span>
                  {isActive && <div className="step-loader" />}
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Render principal ─────────────────────────────────────
  return (
    <motion.div
      className="patient-form-confirmation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Encabezado del paso */}
      <div className="step-header">
        <h2 className="step-title">
          <i className="fas fa-user-check" />
          Tus Datos y Confirmación
        </h2>
        <p className="step-description">
          Completá tu información personal y revisá los detalles antes de confirmar
        </p>
      </div>

      {/* Indicador de progreso interno — solo visible en mobile */}
      <div className="pfc-progress-mobile" role="progressbar" aria-label="Progreso del formulario">
        {FORM_GROUPS.map((group) => (
          <button
            key={group}
            className={`pfc-progress-dot${activeGroup === group ? ' active' : ''}${isGroupComplete(group) && activeGroup !== group ? ' completed' : ''}`}
            onClick={() => setActiveGroup(group)}
            aria-label={`Ir a grupo ${group}`}
            type="button"
          />
        ))}
      </div>

      <div className="step-content">
        <div className="pfc-layout">

          {/* ── Columna izquierda: formulario ── */}
          <div className="pfc-form-column">

            {/* Aviso de autosave (UX_VISION P1-5) — transparencia sobre el guardado local */}
            <p className="pfc-autosave-note">
              <i className="fas fa-floppy-disk" aria-hidden="true" />
              <span>
                Guardamos lo que escribís en este dispositivo para que no lo pierdas si se cierra la página.
                {draftExists && (
                  <>
                    {' '}
                    <button type="button" className="pfc-autosave-clear" onClick={handleDiscardDraft}>
                      Descartar lo guardado
                    </button>
                  </>
                )}
              </span>
            </p>

            {/* GRUPO 1 — Información personal */}
            <div
              className="pfc-group"
              id="group-personal"
              onFocus={() => setActiveGroup('personal')}
            >
              <div className="pfc-group-header">
                <div className="pfc-group-header-left">
                  <div className="pfc-group-icon" aria-hidden="true">
                    <i className="fas fa-user" />
                  </div>
                  <div>
                    <p className="pfc-group-title">Información personal</p>
                    <p className="pfc-group-subtitle">Tus datos de identificación</p>
                  </div>
                </div>
                <div className="pfc-group-status">
                  {isGroupComplete('personal') ? (
                    <span className="pfc-group-badge pfc-group-badge--complete">
                      <i className="fas fa-check" /> Completo
                    </span>
                  ) : (
                    <span className="pfc-group-badge pfc-group-badge--required">Requerido</span>
                  )}
                </div>
              </div>

              <div className="pfc-group-body">
                {/* Nombre y Apellido */}
                <div className="pfc-field-row">
                  <div className="pfc-field">
                    <label className="pfc-label" htmlFor="pfc-nombre">
                      <span className="pfc-required-dot" aria-hidden="true" />
                      Nombre
                    </label>
                    <input
                      id="pfc-nombre"
                      type="text"
                      className={inputClass('nombre')}
                      value={formData.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      onBlur={() => handleBlur('nombre')}
                      placeholder="Tu nombre"
                      autoComplete="given-name"
                    />
                    {touched.nombre && formErrors.nombre && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.nombre}
                      </span>
                    )}
                  </div>

                  <div className="pfc-field">
                    <label className="pfc-label" htmlFor="pfc-apellido">
                      <span className="pfc-required-dot" aria-hidden="true" />
                      Apellido
                    </label>
                    <input
                      id="pfc-apellido"
                      type="text"
                      className={inputClass('apellido')}
                      value={formData.apellido}
                      onChange={(e) => handleInputChange('apellido', e.target.value)}
                      onBlur={() => handleBlur('apellido')}
                      placeholder="Tu apellido"
                      autoComplete="family-name"
                    />
                    {touched.apellido && formErrors.apellido && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.apellido}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email y Teléfono */}
                <div className="pfc-field-row">
                  <div className="pfc-field">
                    <label className="pfc-label" htmlFor="pfc-email">
                      <span className="pfc-required-dot" aria-hidden="true" />
                      Email
                    </label>
                    <input
                      id="pfc-email"
                      type="email"
                      className={inputClass('email')}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      placeholder="tu@email.com"
                      autoComplete="email"
                      inputMode="email"
                    />
                    {touched.email && formErrors.email && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="pfc-field">
                    <label className="pfc-label" htmlFor="pfc-telefono">
                      <span className="pfc-required-dot" aria-hidden="true" />
                      Teléfono
                    </label>
                    <input
                      id="pfc-telefono"
                      type="tel"
                      className={inputClass('telefono')}
                      value={formData.telefono}
                      onChange={(e) => handleInputChange('telefono', e.target.value)}
                      onBlur={() => handleBlur('telefono')}
                      placeholder="+56 9 1234 5678"
                      autoComplete="tel"
                      inputMode="tel"
                    />
                    {touched.telefono && formErrors.telefono && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.telefono}
                      </span>
                    )}
                  </div>
                </div>

                {/* Género — oculto en terapia de pareja, reemplazado por nombre de la pareja */}
                {state.selectedService?.therapyType === 'pareja' ? (
                  <div className="pfc-field-row pfc-field-row--single">
                    <div className="pfc-field">
                      <label className="pfc-label" htmlFor="pfc-nombre-pareja">
                        Nombre de tu pareja
                        <span className="pfc-optional-tag">opcional</span>
                      </label>
                      <input
                        id="pfc-nombre-pareja"
                        type="text"
                        className="pfc-input"
                        value={formData.nombrePareja}
                        onChange={(e) => handleInputChange('nombrePareja', e.target.value)}
                        placeholder="Nombre completo de tu pareja"
                        autoComplete="off"
                      />
                      <span className="pfc-field-hint">
                        <i className="fas fa-lock" aria-hidden="true" />
                        Así tu terapeuta ya sabe quién te acompaña antes de la sesión — dato confidencial.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="pfc-field-row pfc-field-row--single">
                    <div className="pfc-field">
                      <span className="pfc-label">
                        Género
                        <span className="pfc-optional-tag">opcional</span>
                      </span>
                      <div className="pfc-gender-group" role="group" aria-label="Seleccionar género">
                        {GENERO_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`pfc-gender-pill${formData.genero === opt.value ? ' selected' : ''}`}
                            onClick={() => handleInputChange('genero', opt.value)}
                            aria-pressed={formData.genero === opt.value}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Dirección — solo para consultas a domicilio */}
                {state.selectedService?.modality === 'domicilio' && (
                  <div className="pfc-field-row pfc-field-row--single">
                    <div className="pfc-field">
                      <label className="pfc-label" htmlFor="pfc-direccion">
                        <span className="pfc-required-dot" aria-hidden="true" />
                        Dirección donde te visitará el terapeuta
                      </label>
                      <input
                        id="pfc-direccion"
                        type="text"
                        className={inputClass('direccion')}
                        value={formData.direccion}
                        onChange={(e) => handleInputChange('direccion', e.target.value)}
                        onBlur={() => handleBlur('direccion')}
                        placeholder="Calle, número, depto/casa, comuna"
                        autoComplete="street-address"
                      />
                      {touched.direccion && formErrors.direccion && (
                        <span className="pfc-error-msg" role="alert">
                          <i className="fas fa-exclamation-circle" />
                          {formErrors.direccion}
                        </span>
                      )}
                      <span className="pfc-field-hint">
                        <i className="fas fa-lock" aria-hidden="true" />
                        Tu dirección es confidencial: viaja por conexión cifrada y solo la ve tu terapeuta, según la Ley 19.628.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* GRUPO 2 — Motivo de consulta (requerido) */}
            <div
              className="pfc-group"
              id="group-reason"
              onFocus={() => setActiveGroup('reason')}
            >
              <div className="pfc-group-header">
                <div className="pfc-group-header-left">
                  <div className="pfc-group-icon pfc-group-icon--reason" aria-hidden="true">
                    <i className="fas fa-comment-medical" />
                  </div>
                  <div>
                    <p className="pfc-group-title">Motivo de consulta</p>
                    <p className="pfc-group-subtitle">Contanos brevemente qué te trae</p>
                  </div>
                </div>
                <div className="pfc-group-status">
                  {isGroupComplete('reason') ? (
                    <span className="pfc-group-badge pfc-group-badge--complete">
                      <i className="fas fa-check" /> Completo
                    </span>
                  ) : (
                    <span className="pfc-group-badge pfc-group-badge--required">Requerido</span>
                  )}
                </div>
              </div>

              <div className="pfc-group-body">
                {/* Selector visual de motivo principal */}
                <div className="pfc-field-row pfc-field-row--single">
                  <div className="pfc-field">
                    <label className="pfc-label" htmlFor="pfc-motivo">
                      <span className="pfc-required-dot" aria-hidden="true" />
                      Principal motivo
                    </label>
                    <select
                      id="pfc-motivo"
                      className={`pfc-input${touched.motivoConsulta && formErrors.motivoConsulta ? ' pfc-input--invalid' : touched.motivoConsulta && formData.motivoConsulta ? ' pfc-input--valid' : ''}`}
                      value={formData.motivoConsulta}
                      onChange={(e) => handleInputChange('motivoConsulta', e.target.value)}
                      onBlur={() => handleBlur('motivoConsulta')}
                    >
                      <option value="">Seleccioná el principal motivo...</option>
                      {MOTIVO_OPTIONS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    {touched.motivoConsulta && formErrors.motivoConsulta && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.motivoConsulta}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* GRUPO 3 — Confirmaciones legales */}
            <div
              className="pfc-group"
              id="group-legal"
              onFocus={() => setActiveGroup('legal')}
            >
              <div className="pfc-group-header">
                <div className="pfc-group-header-left">
                  <div className="pfc-group-icon pfc-group-icon--legal" aria-hidden="true">
                    <i className="fas fa-shield-alt" />
                  </div>
                  <div>
                    <p className="pfc-group-title">Privacidad y términos</p>
                    <p className="pfc-group-subtitle">Necesitamos tu confirmación</p>
                  </div>
                </div>
                <div className="pfc-group-status">
                  {isGroupComplete('legal') ? (
                    <span className="pfc-group-badge pfc-group-badge--complete">
                      <i className="fas fa-check" /> Aceptado
                    </span>
                  ) : (
                    <span className="pfc-group-badge pfc-group-badge--required">Requerido</span>
                  )}
                </div>
              </div>

              <div className="pfc-group-body">
                {/* Consentimiento de datos clínicos/sensibles (obligatorio) */}
                <div className="pfc-consent-item">
                  <div className="pfc-checkbox-wrap">
                    <input
                      type="checkbox"
                      id="pfc-consentimiento-datos-clinicos"
                      checked={formData.consentimientoDatosClinicos}
                      onChange={(e) => handleInputChange('consentimientoDatosClinicos', e.target.checked)}
                      className={formErrors.consentimientoDatosClinicos ? 'invalid' : ''}
                    />
                    <div
                      className="pfc-checkbox-visual"
                      onClick={() => handleInputChange('consentimientoDatosClinicos', !formData.consentimientoDatosClinicos)}
                      role="presentation"
                    >
                      {formData.consentimientoDatosClinicos && <i className="fas fa-check" />}
                    </div>
                  </div>
                  <div className="pfc-consent-text">
                    <label className="pfc-consent-label" htmlFor="pfc-consentimiento-datos-clinicos">
                      Autorizo el tratamiento de mis datos clínicos y de salud
                    </label>
                    <p className="pfc-consent-desc">
                      Tus datos de salud serán tratados con confidencialidad, según la Ley 21.719 de Protección de Datos Personales de Chile.
                    </p>
                    {formErrors.consentimientoDatosClinicos && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.consentimientoDatosClinicos}
                      </span>
                    )}
                  </div>
                </div>

                {/* Consentimiento de contacto/marketing (opcional, opt-in explícito) */}
                <div className="pfc-consent-item">
                  <div className="pfc-checkbox-wrap">
                    <input
                      type="checkbox"
                      id="pfc-consentimiento-marketing"
                      checked={formData.consentimientoContactoMarketing}
                      onChange={(e) => handleInputChange('consentimientoContactoMarketing', e.target.checked)}
                    />
                    <div
                      className="pfc-checkbox-visual"
                      onClick={() => handleInputChange('consentimientoContactoMarketing', !formData.consentimientoContactoMarketing)}
                      role="presentation"
                    >
                      {formData.consentimientoContactoMarketing && <i className="fas fa-check" />}
                    </div>
                  </div>
                  <div className="pfc-consent-text">
                    <label className="pfc-consent-label" htmlFor="pfc-consentimiento-marketing">
                      Acepto ser contactado/a con fines de marketing (opcional)
                    </label>
                    <p className="pfc-consent-desc">
                      Podés recibir novedades o comunicaciones comerciales. No es requisito para agendar tu cita.
                    </p>
                  </div>
                </div>

                {/* Términos y condiciones */}
                <div className="pfc-consent-item">
                  <div className="pfc-checkbox-wrap">
                    <input
                      type="checkbox"
                      id="pfc-terminos"
                      checked={formData.terminosCondiciones}
                      onChange={(e) => handleInputChange('terminosCondiciones', e.target.checked)}
                      className={formErrors.terminosCondiciones ? 'invalid' : ''}
                    />
                    <div
                      className="pfc-checkbox-visual"
                      onClick={() => handleInputChange('terminosCondiciones', !formData.terminosCondiciones)}
                      role="presentation"
                    >
                      {formData.terminosCondiciones && <i className="fas fa-check" />}
                    </div>
                  </div>
                  <div className="pfc-consent-text">
                    <label className="pfc-consent-label" htmlFor="pfc-terminos">
                      Acepto los términos y condiciones del servicio
                    </label>
                    <p className="pfc-consent-desc">
                      Entiendo la política de cancelación (48 hrs de anticipación) y las condiciones del servicio de psicología.
                    </p>
                    {formErrors.terminosCondiciones && (
                      <span className="pfc-error-msg" role="alert">
                        <i className="fas fa-exclamation-circle" />
                        {formErrors.terminosCondiciones}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* GRUPO 4 — Método de pago: al final, después de completar los datos */}
            <div className="pfc-group">
              <div className="pfc-group-header">
                <div className="pfc-group-header-left">
                  <div className="pfc-group-icon" aria-hidden="true">
                    <i className="fas fa-wallet" />
                  </div>
                  <div>
                    <p className="pfc-group-title">Método de pago</p>
                    <p className="pfc-group-subtitle">Pagás en la consulta o al confirmar</p>
                  </div>
                </div>
              </div>

              <div className="pfc-group-body">
                <PaymentMethodSelector
                  metodo={state.metodoPagoDeclarado}
                  onChange={setMetodoPagoDeclarado}
                  tipo={state.selectedService?.therapyType ?? ''}
                  duracion={state.selectedService?.duration ?? 0}
                  modalidad={state.selectedService?.modality ?? 'presencial'}
                  fechaHora={state.selectedTimeSlot?.date}
                />
              </div>
            </div>
          </div>

          {/* ── Columna derecha: resumen sticky ── */}
          <div className="pfc-summary-column">
            <div className="pfc-summary-card">
              {/* Header del resumen */}
              <div className="pfc-summary-header">
                <div className="pfc-summary-icon" aria-hidden="true">
                  <i className="fas fa-clipboard-check" />
                </div>
                <div>
                  <p className="pfc-summary-title">Tu cita</p>
                  <p className="pfc-summary-subtitle">Verificá los detalles</p>
                </div>
                {/* Toggle visible solo en mobile */}
                <button
                  type="button"
                  className={`pfc-summary-toggle${summaryCollapsed ? ' collapsed' : ''}`}
                  onClick={() => setSummaryCollapsed((v) => !v)}
                  aria-expanded={!summaryCollapsed}
                  aria-controls="pfc-summary-body"
                  aria-label={summaryCollapsed ? 'Ver resumen' : 'Ocultar resumen'}
                >
                  <i className="fas fa-chevron-down" />
                </button>
              </div>

              <div
                className={`pfc-summary-body${summaryCollapsed ? ' collapsed' : ''}`}
                id="pfc-summary-body"
              >
                {/* Terapeuta — avatar CSS con iniciales */}
                <div className="pfc-therapist-row">
                  <div className="pfc-therapist-avatar" aria-hidden="true">
                    {getInitials(state.selectedTherapist?.name)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p className="pfc-therapist-name">{state.selectedTherapist?.name}</p>
                    <p className="pfc-therapist-title">{state.selectedTherapist?.title}</p>
                    {state.selectedTherapist?.specialties && (
                      <div className="pfc-specialties">
                        {state.selectedTherapist.specialties.slice(0, 2).map((s, i) => (
                          <span key={i} className="pfc-specialty-badge">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Servicio */}
                <div className="pfc-summary-section">
                  <div className="pfc-summary-section-icon" aria-hidden="true">
                    <i className="fas fa-stethoscope" />
                  </div>
                  <div className="pfc-summary-section-content">
                    <p className="pfc-summary-section-label">Consulta</p>
                    <p className="pfc-summary-section-value">{state.selectedService?.name}</p>
                    <p className="pfc-summary-section-sub">
                      {state.selectedService?.duration} min &middot;{' '}
                      {state.selectedService?.modality === 'presencial'
                        ? 'Presencial'
                        : state.selectedService?.modality === 'domicilio'
                        ? 'Domicilio'
                        : 'Online'}
                    </p>
                    {state.selectedService?.modality === 'presencial' && (
                      <p className="pfc-summary-section-sub">
                        <i className="fas fa-map-marker-alt" style={{ marginRight: 4 }} />
                        {CLINIC_OFFICE_ADDRESS}
                      </p>
                    )}
                  </div>
                </div>

                {/* Fecha y hora */}
                <div className="pfc-summary-section">
                  <div className="pfc-summary-section-icon" aria-hidden="true">
                    <i className="fas fa-calendar-alt" />
                  </div>
                  <div className="pfc-summary-section-content">
                    <p className="pfc-summary-section-label">Fecha y hora</p>
                    <p className="pfc-summary-section-value">
                      {state.selectedTimeSlot ? formatDate(state.selectedTimeSlot.date) : '—'}
                    </p>
                    <p className="pfc-summary-section-sub">{state.selectedTimeSlot?.time} hrs</p>
                  </div>
                </div>
              </div>

              {/* Precio y método de pago — fuera del acordeón: en mobile debe verse siempre, no depende del toggle */}
              <div className="pfc-pricing-block">
                <div className="pfc-price-row">
                  <span>Precio consulta</span>
                  <span>${precioFinal.toLocaleString('es-CL')}</span>
                </div>
                <div className="pfc-price-row pfc-price-row--total">
                  <span>Total a pagar</span>
                  <span>${precioFinal.toLocaleString('es-CL')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones */}
      <div className="pfc-actions">
        <button
          type="button"
          className="pfc-btn-back"
          onClick={onPrevious}
          disabled={isProcessing}
        >
          <i className="fas fa-arrow-left" />
          Anterior
        </button>

        <button
          type="button"
          className="pfc-btn-confirm"
          onClick={handleConfirm}
          disabled={isProcessing || !isFormValid()}
          aria-busy={isProcessing}
        >
          <i className="fas fa-check" />
          Confirmar Cita
        </button>
      </div>
    </motion.div>
  );
};

export default PatientFormConfirmation;
